import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';

const LocationForm = ({ location, areas, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: location?.name || '',
    slug: location?.slug || '',
    is_active: location?.is_active ?? true,
    is_default: location?.is_default ?? true,
    area_id: location?.area_id || '',
    area_ids: location?.areas?.map(a => a.id) || []
  });

  // Auto-generate slug when name or primary area changes
  useEffect(() => {
    if (!location && formData.name && formData.area_id) {
      const selectedArea = areas.find(a => a.id === parseInt(formData.area_id));
      if (selectedArea) {
        const autoSlug = `${selectedArea.abbreviation.toLowerCase()}-${formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`;
        setFormData(prev => ({ ...prev, slug: autoSlug }));
      }
    }
  }, [formData.name, formData.area_id, location, areas]);

  // Ensure primary area is in area_ids
  useEffect(() => {
    if (formData.area_id && !formData.area_ids.includes(parseInt(formData.area_id))) {
      setFormData(prev => ({
        ...prev,
        area_ids: [...prev.area_ids, parseInt(formData.area_id)]
      }));
    }
  }, [formData.area_id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.name.trim()) {
      alert('Name is required');
      return;
    }
    
    if (!formData.area_id) {
      alert('Primary area is required');
      return;
    }

    onSubmit(formData);
  };

  const handleAreaToggle = (areaId) => {
    const id = parseInt(areaId);
    
    // Don't allow removing primary area
    if (id === parseInt(formData.area_id)) {
      alert("Cannot remove primary area from area list");
      return;
    }

    setFormData(prev => ({
      ...prev,
      area_ids: prev.area_ids.includes(id)
        ? prev.area_ids.filter(aid => aid !== id)
        : [...prev.area_ids, id]
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Name *
        </label>
        <input
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter location name"
          required
        />
      </div>

      <div>
        <label htmlFor="area_id" className="block text-sm font-medium text-gray-700 mb-1">
          Primary Area *
        </label>
        <select
          id="area_id"
          value={formData.area_id}
          onChange={(e) => setFormData({ ...formData, area_id: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        >
          <option value="">Select primary area...</option>
          {areas.map(area => (
            <option key={area.id} value={area.id}>
              {area.name} ({area.abbreviation})
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
          Slug (auto-generated)
        </label>
        <input
          id="slug"
          type="text"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Auto-generated from name and area"
        />
        <p className="mt-1 text-sm text-gray-500">
          Leave blank to auto-generate from name and primary area
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Areas (select all that apply)
        </label>
        <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
          {areas.map(area => (
            <label key={area.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
              <input
                type="checkbox"
                checked={formData.area_ids.includes(area.id)}
                onChange={() => handleAreaToggle(area.id)}
                disabled={area.id === parseInt(formData.area_id)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">
                {area.name} ({area.abbreviation})
                {area.id === parseInt(formData.area_id) && (
                  <span className="text-blue-600 font-medium"> (Primary)</span>
                )}
              </span>
            </label>
          ))}
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Primary area is automatically included and cannot be removed
        </p>
      </div>

      <div className="space-y-3">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Active</span>
        </label>

        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_default}
            onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Default Location</span>
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button type="submit">
          {location ? 'Update' : 'Create'} Location
        </Button>
      </div>
    </form>
  );
};

export default LocationForm;